import { Component, OnDestroy, ViewChild } from '@angular/core';
import { AbstractControl, NgForm } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { isExternalLink } from '../../utils/util';
import { ThfI18nPipe, ThfModalAction, ThfModalComponent, ThfRadioGroupOption } from '@totvs/thf-ui';

import { ThfModalPasswordRecovery } from './interfaces/thf-modal-password-recovery.interface';
import { ThfModalPasswordRecoveryBaseComponent } from './thf-modal-password-recovery-base.component';
import { ThfModalPasswordRecoveryModalContent } from './enums/thf-modal-password-recovery-modal-content.enum';
import { ThfModalPasswordRecoveryService } from './thf-modal-password-recovery.service';
import { ThfModalPasswordRecoveryType } from './enums/thf-modal-password-recovery-type.enum';

/**
 * @docsExtends ThfModalPasswordRecoveryBaseComponent
 *
 * @example
 *
 * <example name="thf-modal-password-recovery-basic" title="Totvs Modal Password Recovery Basic">
 *  <file name="sample-thf-modal-password-recovery-basic/sample-thf-modal-password-recovery-basic.component.html"> </file>
 *  <file name="sample-thf-modal-password-recovery-basic/sample-thf-modal-password-recovery-basic.component.ts"> </file>
 * </example>
 *
 * <example name="thf-modal-password-recovery-labs" title="Totvs Modal Password Recovery Labs">
 *  <file name="sample-thf-modal-password-recovery-labs/sample-thf-modal-password-recovery-labs.component.html"> </file>
 *  <file name="sample-thf-modal-password-recovery-labs/sample-thf-modal-password-recovery-labs.component.ts"> </file>
 * </example>
 *
 * <example name="thf-modal-password-recovery-request" title="Totvs Modal Password Recovery Request">
 *  <file name="sample-thf-modal-password-recovery-request/sample-thf-modal-password-recovery-request.component.html"> </file>
 *  <file name="sample-thf-modal-password-recovery-request/sample-thf-modal-password-recovery-request.component.ts"> </file>
 * </example>
 */

@Component({
  selector: 'thf-modal-password-recovery',
  templateUrl: './thf-modal-password-recovery.component.html'
})

export class ThfModalPasswordRecoveryComponent extends ThfModalPasswordRecoveryBaseComponent implements OnDestroy {

  chosenTypeFormOption: string = ThfModalPasswordRecoveryType.Email;
  codeMask: string = '9 9 9 9 9 9';
  control: AbstractControl;
  emailModal: boolean = true;
  emailModalPhrases = {};
  endpoint: string = '.';
  invalidCode: boolean = false;
  invalidEmail: boolean = false;
  modalTitle: string;
  modalType: ThfModalPasswordRecoveryModalContent = ThfModalPasswordRecoveryModalContent.Email;
  showCustomCodeError: boolean;
  smsCodeErrorMessagePhrase: string;
  submittedCodeValue = {} as ThfModalPasswordRecovery;
  submittedContactValue = {} as ThfModalPasswordRecovery;

  private passwordRecoverySubscription: Subscription;
  private smsBodyResponse;
  private smsCodeSubscription: Subscription;

  @ViewChild('emailForm', { static: false }) emailForm: NgForm;

  @ViewChild('recoveryModal', { static: true }) recoveryModalElement: ThfModalComponent;

  @ViewChild('smsCodeForm', { static: false }) smsCodeForm: NgForm;

  primaryAction: ThfModalAction = { label: undefined, action: () => { } };

  secondaryAction: ThfModalAction = { label: undefined, action: () => { } };

  typeFormOptions: Array<ThfRadioGroupOption> = [
    { label: 'e-mail', value: ThfModalPasswordRecoveryType.Email },
    { label: 'SMS', value: ThfModalPasswordRecoveryType.SMS }
  ];

  constructor(
    private router: Router,
    private thfI18nPipe: ThfI18nPipe,
    private thfModalPasswordRecoveryService: ThfModalPasswordRecoveryService
    ) {
    super();
  }

  ngOnDestroy() {
    if (this.passwordRecoverySubscription) {
      this.passwordRecoverySubscription.unsubscribe();
    }

    if (this.smsCodeSubscription) {
      this.smsCodeSubscription.unsubscribe();
    }
  }

  completed() {
    this.cancelAction();
  }

  formModelChangesCheck(form: NgForm) {
    const invalidForm = form.invalid;
    this.invalidEmail = invalidForm && form.dirty;
    this.primaryAction.disabled = invalidForm;

    if (this.modalType === ThfModalPasswordRecoveryModalContent.SMSCode) {
      const codeError: boolean = this.codeError !== undefined && this.codeError !== '';
      this.showCustomCodeError = codeError && form.pristine;
    }
  }

  getInputType(type) {
    this.type = type;
    this.pipeModalPhrases();
    setTimeout(() => {
      this.control = this.emailForm.controls[type];
      this.formModelChangesCheck(this.emailForm);
      this.resetFormFields(this.control);
    });
  }

  open() {
    const control = this.checkFormType(this.type);
    this.control = this.emailForm.controls[control];
    this.setEmailModalPhrasesAndActions();
    this.formModelChangesCheck(this.emailForm);
    this.recoveryModalElement.open();
  }

  openConfirmation() {
    this.modalTitle = this.literals.emailSentTitle;
    this.modalType = ThfModalPasswordRecoveryModalContent.Confirmation;
    this.setActions(this.cancelAction, this.literals.closeButton, this.submitAction, this.literals.resendEmailButton, false);
  }

  openSmsCode() {
    this.modalTitle = this.literals.typeCodeTitle;
    this.modalType = ThfModalPasswordRecoveryModalContent.SMSCode;
    this.setActions(this.submitSmsCodeAction, this.literals.continueButton, this.cancelAction, this.literals.cancelButton, true);

    setTimeout(() => {
      this.control = this.smsCodeForm.controls['sms'];
      this.formModelChangesCheck(this.smsCodeForm);
    });
  }

  resendSmsCode() {
    this.incrementRetryAttempts();
    if (this.urlRecovery) {
      this.submitActionRequest(this.submittedContactValue, this.type);
    } else {
      this.submit.emit(this.submittedContactValue);
    }
  }

  private assignSmsResponse(responseObj) {
    this.smsBodyResponse = Object.assign({}, {hash: responseObj.hash});
    if (responseObj.urlValidationCode) {
      this.smsBodyResponse = Object.assign(this.smsBodyResponse, {urlValidationCode: responseObj.urlValidationCode});
    }
  }

  private cancelAction() {
    this.resetFormFields(this.control);
    this.submittedContactValue = {};

    this.chosenTypeFormOption = ThfModalPasswordRecoveryType.Email;
    this.modalType = ThfModalPasswordRecoveryModalContent.Email;
    this.type = this.modalPasswordRecoveryTypeAll ? ThfModalPasswordRecoveryType.All : this.type;
    this.recoveryModalElement.close();
  }

  private checkFormType(type: ThfModalPasswordRecoveryType) {
    return type !== ThfModalPasswordRecoveryType.All ? type : ThfModalPasswordRecoveryType.Email;
  }

  private formReset(control: AbstractControl) {
    control.markAsPristine();
    control.markAsUntouched();
    control.updateValueAndValidity();
  }

  private getEmitValue(type: ThfModalPasswordRecoveryType) {
    return type === ThfModalPasswordRecoveryType.SMS ? this.phone : this.email;
  }

  private incrementRetryAttempts() {
    this.submittedContactValue.retry = (this.submittedContactValue.retry + 1 || 1);
  }

  private openExternalLink(url, queryParam) {
    window.open(`${url}?token=${queryParam}`, '_self');
  }

  private openInternalLink(url, endpoint, queryParam) {
    this.router.navigate([`${url}/${endpoint}`], { queryParams: { token: queryParam } });
  }

  private pipeModalPhrases() {
    if (this.type === ThfModalPasswordRecoveryType.SMS) {
      this.emailModalPhrases['firstPhrase'] = this.setPipeArguments(this.literals.recoveryPasswordPhrase, this.literals.sms);
      this.emailModalPhrases['secondPhrase'] = this.setPipeArguments(this.literals.supportContact, this.literals.telephone);
    } else {
      this.emailModalPhrases['firstPhrase'] = this.setPipeArguments(this.literals.recoveryPasswordPhrase, this.literals.email);
      this.emailModalPhrases['secondPhrase'] = this.setPipeArguments(this.literals.supportContact, this.literals.email);
    }
  }

  private redirectToChangePassword(recoveryToken: ThfModalPasswordRecovery) {
    const urlChangePassword = recoveryToken.urlChangePassword;
    if (urlChangePassword) {
      isExternalLink(urlChangePassword) ?
      this.openExternalLink(urlChangePassword, recoveryToken.token) :
      this.openInternalLink(this.urlRecovery, urlChangePassword, recoveryToken.token);
    } else {
      const changePasswordEndpoint = 'changePassword';
      this.openInternalLink(this.urlRecovery, changePasswordEndpoint, recoveryToken.token);
    }
  }

  private resetFormFields(control) {
    this.formReset(control);
    this.email = undefined;
    this.phone = undefined;
    this.smsCode = undefined;
  }

  private setActions(primaryAction, primarylabel, secondaryAction, secondaryLabel, disabled) {
    this.primaryAction.action = () => primaryAction.call(this);
    this.primaryAction.label = primarylabel;
    this.secondaryAction.action = () => secondaryAction.call(this);
    this.secondaryAction.label = secondaryLabel;
    this.primaryAction.disabled = disabled;
  }

  private setEmailModalPhrasesAndActions() {
    this.modalTitle = this.literals.forgotPasswordTitle;
    this.pipeModalPhrases();
    this.modalPasswordRecoveryTypeAll = this.type === ThfModalPasswordRecoveryType.All;
    this.setActions(this.submitAction, this.literals.sendButton, this.cancelAction, this.literals.cancelButton, true);
  }

  private setRequestEndpoint(urlValidationCode?: string) {
    const endpoint = urlValidationCode || 'validation';

    return `${this.urlRecovery}/${endpoint}`;
  }

  private setPipeArguments(literalAttr: string, arg: string) {
    return this.thfI18nPipe.transform(literalAttr, arg);
  }

  private submitAction() {
    this.modalType === ThfModalPasswordRecoveryModalContent.Confirmation ? this.incrementRetryAttempts() : this.formReset(this.control);
    this.submittedContactValue[this.checkFormType(this.type)] = this.getEmitValue(this.type);
    if (this.urlRecovery) {
      this.submitActionRequest(this.submittedContactValue, this.type);
    } else {
      this.submit.emit(this.submittedContactValue);
    }
  }

  private submitActionRequest(data: ThfModalPasswordRecovery, modalType: ThfModalPasswordRecoveryType) {
    const params = modalType === ThfModalPasswordRecoveryType.SMS ? { type: 'sms' } : undefined;

    this.passwordRecoverySubscription =
    this.thfModalPasswordRecoveryService.post(this.urlRecovery, data, params)
    .subscribe(response => {
      if ((modalType === ThfModalPasswordRecoveryType.Email || modalType === ThfModalPasswordRecoveryType.All) && response.status === 204) {
        this.openConfirmation();
      } else if (modalType === ThfModalPasswordRecoveryType.SMS && response.status === 200) {
        this.assignSmsResponse(response.body);
        this.openSmsCode();
      }
    });
  }

  private submitSmsCodeAction() {
    this.submittedCodeValue.code = this.smsCode;

    if (this.urlRecovery) {
      this.submittedCodeValue = Object.assign(this.submittedCodeValue, {hash: this.smsBodyResponse.hash});
      this.submitSmsCodeRequest(this.submittedCodeValue);

    } else {
      this.codeSubmit.emit(this.submittedCodeValue);
    }
    this.resetFormFields(this.control);
  }

  private submitSmsCodeRequest(data: ThfModalPasswordRecovery) {
    this.smsCodeSubscription = this.thfModalPasswordRecoveryService
    .post(this.setRequestEndpoint(this.smsBodyResponse.urlValidationCode), data)
    .subscribe(
      response => {
        const successStatus = response.status === 200;
        if (successStatus) {
          this.completed();
          this.redirectToChangePassword(response.body);
        }
      },
      error => {
        this.codeError = error.error.message;
        this.openSmsCode();
      }
    );
  }

}
